import React, { useContext, useEffect, useState } from 'react'
import { AppSettings } from '../../../config/app-settings'
import {
  useGetTelegramLinksQuery,
  useMarkTelegramMutation
} from '../../../redux/apis/linkManagement/telegram/lMTelegramApiSlice'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Loader from '../../pages/loader'
import ReusableTable from '../../../components/other/ReuseableTable'
import { Modal, Button } from 'antd'
import generateExcel from '../../../helpers/generateExcel'

const TelegramCampaignReview = () => {
  const context = useContext(AppSettings)
  const { id } = useParams()

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  })

  const [selectedStatuses, setSelectedStatuses] = useState({})
  const [markTelegram, { isLoading: markLoading }] = useMarkTelegramMutation()

  const { data, isLoading, isFetching, error } = useGetTelegramLinksQuery({
    expand: ['inserted_by'].join(','),
    page: pagination.current,
    page_size: pagination.pageSize,
    fields: ['inserted_by', 'url', 'id'].join(','),
    campaigns: id,
    reviewed: false
  })

  useEffect(() => {
    if (error) toast.error('Error fetching links')
  }, [error])

  const handleTableChange = paginationInfo => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    })
  }

  const handleBulkSelect = status => {
    const updated = {}
    data?.data?.results?.forEach(row => {
      updated[row.id] = status
    })
    setSelectedStatuses(updated)
  }

  const handleSubmit = async () => {
    const payload = Object.entries(selectedStatuses).map(([id, status]) => {
      const record = data?.data?.results?.find(
        item => item.id.toString() === id
      )
      return {
        url: record?.url,
        status
      }
    })

    try {
      const response = await markTelegram(payload).unwrap()

      // Display the message in toast
      if (response?.success) {
        toast.success(response?.message || 'Links updated successfully')
        setSelectedStatuses({})
      } else {
        toast.error(response?.message || 'Failed to update links')

        // Generate Excel with errors data if errors exist
        if (response?.errors && Object.keys(response.errors).length > 0) {
          const errorsData = Object.entries(response.errors).map(([url, errorInfo]) => ({
            URL: url.trim(),
            Success: errorInfo.success ? 'Yes' : 'No',
            Message: errorInfo.message,
            Field: errorInfo.errors?.[0]?.field || '',
            Error: errorInfo.errors?.[0]?.message || ''
          }))

          generateExcel(errorsData, `telegram_marking_errors_${new Date().toISOString().split('T')[0]}`)
          toast.info('Error details downloaded as Excel file')
        }
      }
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to update links.')

      // Handle errors in catch block if they exist
      if (error?.data?.errors && Object.keys(error.data.errors).length > 0) {
        const errorsData = Object.entries(error.data.errors).map(([url, errorInfo]) => ({
          URL: url.trim(),
          Success: errorInfo.success ? 'Yes' : 'No',
          Message: errorInfo.message,
          Field: errorInfo.errors?.[0]?.field || '',
          Error: errorInfo.errors?.[0]?.message || ''
        }))

        generateExcel(errorsData, `telegram_marking_errors_${new Date().toISOString().split('T')[0]}`)
        toast.info('Error details downloaded as Excel file')
      }
    }
  }

  useEffect(() => {
    if (data?.data?.count === 0) {
      toast.info('All links processed. Closing tab...')

      const timeout = setTimeout(() => {
        window.close()
      }, 1500) // 1.5 seconds

      return () => clearTimeout(timeout) // cleanup if component unmounts
    }
  }, [data?.data?.count])

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', sorter: true, search: true },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      render: text => (
        <a href={text} target='_blank' rel='noreferrer'>
          <p>{decodeURIComponent(text)}</p>
        </a>
      )
    },
    {
      title: 'Inserted BY',
      dataIndex: 'inserted_by',
      key: 'inserted_by',
      render: text => (
        <p>
          {text?.first_name} {text?.last_name}
        </p>
      )
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const currentStatus = selectedStatuses[record.id] || ''
        return (
          <div className='flex gap-1 flex-col'>
            {['related', 'unrelated', 'removed'].map(status => (
              <label key={status} className='flex gap-2'>
                <input
                  type='radio'
                  name={`status-${record.id}`}
                  value={status}
                  checked={currentStatus === status}
                  onChange={() =>
                    setSelectedStatuses(prev => ({
                      ...prev,
                      [record.id]: status
                    }))
                  }
                />
                <span>{status}</span>
              </label>
            ))}
          </div>
        )
      }
    }
  ]

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [context])

  return (
    <div className='w-full flex flex-col'>
      <Loader isLoading={isLoading || markLoading || isFetching} />

      <div className='row'>
        <ReusableTable
          columns={columns}
          data={data?.data?.results}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.data?.count,
            showSizeChanger: true
          }}
          onChange={handleTableChange}
        />
      </div>

      <div className='flex gap-4 mt-4'>
        {['related', 'unrelated', 'removed'].map(status => (
          <Button
            key={status}
            type='default'
            onClick={() => handleBulkSelect(status)}
          >
            Select All as {status}
          </Button>
        ))}

        <button
          className='btn btn-theme'
          onClick={handleSubmit}
          disabled={Object.keys(selectedStatuses).length === 0}
        >
          Submit
        </button>
      </div>
    </div>
  )
}

export default TelegramCampaignReview
